import {
    LeadReportGroupBy,
    LeadReportStatus,
} from "helpers/leads/generalReports";

export type DatePreset = "last7" | "last30" | "last90" | "custom";

export interface LeadGeneralReportFilters {
    datePreset: DatePreset;
    startDate: string | null;
    endDate: string | null;
    statuses: LeadReportStatus[];
    groupBy: LeadReportGroupBy;
    connectionIds?: number[];
}

export interface SummaryMetrics {
    totalLeads: number;
    sent: number;
    failed: number;
    duplicated: number;
    sentSuccessRate: number;
    failedAnomalyPct: number;
    duplicatedRate: number;
}

export interface DailyTimePoint {
    date: string;
    sent: number;
    failed: number;
    duplicated: number;
    total: number;
}

export interface SourceBreakdownItem {
    sourceType: string;
    name: string;
    category: string;
    total: number;
    sent: number;
    failed: number;
    accentColor: string;
}

export interface ConnectionTableRow {
    id: number;
    connectionName: string;
    sourceType: string;
    destinationType: string;
    status: number;
    total: number;
    sent: number;
    failed: number;
    duplicated: number;
    dailyBreakdown: DailyTimePoint[];
}

export interface LeadGeneralReportData {
    summary: SummaryMetrics;
    daily: DailyTimePoint[];
    sourceBreakdown?: SourceBreakdownItem[];
    connections?: ConnectionTableRow[];
}

export const STATUS_COLORS: Record<
    LeadReportStatus,
    { bg: string; text: string; label: string }
> = {
    RECEIVED: { bg: "#E0F2FE", text: "#0369A1", label: "Received" },
    PROCESSED: { bg: "#E0E7FF", text: "#4338CA", label: "Processed" },
    SENT: { bg: "#DCFCE7", text: "#166534", label: "Sent" },
    PROCESS_FAILED: {
        bg: "#FEE2E2",
        text: "#B91C1C",
        label: "Process Failed",
    },
    SEND_FAILED: { bg: "#FEE2E2", text: "#B91C1C", label: "Send Failed" },
    DUPLICATED: { bg: "#F3F4F6", text: "#374151", label: "Duplicated" },
    FAILED_TO_FETCH_DATA: {
        bg: "#FEE2E2",
        text: "#B91C1C",
        label: "Failed to Fetch Data",
    },
};

/**
 * Every selectable status, in display order. There is deliberately no "All
 * Statuses" entry: an empty selection already means unfiltered, and the
 * select's own header owns the select-all affordance.
 */
export const ALL_STATUSES: LeadReportStatus[] = [
    "RECEIVED",
    "PROCESSED",
    "SENT",
    "PROCESS_FAILED",
    "SEND_FAILED",
    "DUPLICATED",
    "FAILED_TO_FETCH_DATA",
];

export const STATUS_OPTIONS: { value: LeadReportStatus; label: string }[] =
    ALL_STATUSES.map((status) => ({
        value: status,
        label: STATUS_COLORS[status].label,
    }));

export const DEFAULT_FILTERS: LeadGeneralReportFilters = {
    datePreset: "last30",
    startDate: null,
    endDate: null,
    statuses: [...ALL_STATUSES],
    groupBy: "connection",
};
